import { useEffect, useRef } from "react";
import { POST_MESSAGE_IN_TYPES, POST_MESSAGE_OUT_TYPES } from "../../../../../../../helpers/FeatureControlsHelper";
import { PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS } from "../../../../../../../helpers/Colours";
import config from "../../../../../../../config";
import { getPreviewCustomisationVars } from "../../../../v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";

const hasValidImageSource = (imageConfig) => {
    if (Array.isArray(imageConfig)) {
        return imageConfig.some((image) => typeof image?.src === 'string' && image.src.trim() !== '');
    }

    return typeof imageConfig?.src === 'string' && imageConfig.src.trim() !== '';
};

const sanitizeParticleImageShapes = (value) => {
    if (!value || typeof value !== 'object') {
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item) => sanitizeParticleImageShapes(item));
        return;
    }

    if (value.shape && typeof value.shape === 'object') {
        const shapeType = value.shape.type;
        const usesImageType = shapeType === 'image' || (Array.isArray(shapeType) && shapeType.includes('image'));

        if (usesImageType && !hasValidImageSource(value.shape.image)) {
            value.shape.type = 'circle';
            delete value.shape.image;
        }
    }

    Object.values(value).forEach((child) => sanitizeParticleImageShapes(child));
};

const ParticleEffects = (props) => {
    const campaignData = props.campaignData;
    const featureData = props.node.featureData;

    const featureId = props.node?.parent?.id ?? props.node.id;
    const layerIndex = props.layerIndex ?? 0;
    const iframeId = (props.overrideContainerId ? `${props.overrideContainerId}_iframe` : featureId) + '_' + layerIndex;
    const customisationVars = props.node.customisationVars;
    const stringifiedSettingsRef = useRef(null);
    const refreshTimeoutRef = useRef(null);
    const hasStartedRef = useRef(false);

    const featureVars = {
        "featureControl": {
            "autoEnd": false,
            "autoEndDelay": 20
        },
        "featureURL": "/disruptor/particles/",
        "featureType": "disruptor",
        "featureName": "Particle Effects",
        "baseUrls": {
            "frontend": window.location.origin,
            "backend": config.apiBaseUrl
        },
        "theme": {
            "id": ""
        }
    }

    const postMessageToFrame = (type, detail) => {
        const frameRef = document.getElementById(iframeId);
        if (frameRef) {
            frameRef.contentWindow.postMessage({ type: type, detail: detail }, '*');
        }
    };

    useEffect(() => {
        const processPostedMessages = (event) => {
            const frameRef = document.getElementById(iframeId);
            if (!frameRef?.contentWindow || event.source !== frameRef.contentWindow || !event.data?.type) {
                return;
            }

            switch (event.data.type) {
                case POST_MESSAGE_IN_TYPES.HANDSHAKE: {
                    event.source.postMessage(
                        { type: POST_MESSAGE_OUT_TYPES.CONNECTED, detail: 'Howdy!' },
                        event.origin
                    );

                    const customisationData = getPreviewCustomisationVars(
                        featureData,
                        featureId,
                        campaignData?._id,
                        customisationVars
                    );
                    if (customisationData?.audio?.audio?.toggleSFX?.[0]) {
                        customisationData.audio.audio.toggleSFX[0].confettiPop = false;
                        customisationData.audio.audio.toggleSFX[0].explosions = false;
                        customisationData.audio.audio.toggleSFX[0].playBgAudio = false;
                        customisationData.audio.audio.toggleSFX[0].playEndAudio = false;
                        customisationData.audio.audio.toggleSFX[0].playStartAudio = false;
                        customisationData.audio.audio.toggleSFX[0].rocketFired = false;
                    }
                    sanitizeParticleImageShapes(customisationData);
                    PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS(customisationData, featureData, campaignData);
                    postMessageToFrame(POST_MESSAGE_OUT_TYPES.CUSTOMISATION_VARS_RECEIVED, customisationData);
                    break;
                }
                case POST_MESSAGE_IN_TYPES.FEATURE_LOADED:
                    if (hasStartedRef.current) {
                        return;
                    }

                    hasStartedRef.current = true;
                    postMessageToFrame(POST_MESSAGE_OUT_TYPES.SETTINGS_VARS_RECEIVED, featureVars);
                    setTimeout(() => {
                        postMessageToFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: "START_PARTICLES", extraData: 0 });
                    }, 100);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("message", processPostedMessages);
        stringifiedSettingsRef.current = JSON.stringify(customisationVars);

        return () => {
            window.removeEventListener("message", processPostedMessages);
            stringifiedSettingsRef.current = null;
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
            hasStartedRef.current = false;
        };
    }, [campaignData, customisationVars, featureData, featureVars, iframeId]);

    // Remove console.logs from the iframe to prevent performance issues when rapidly changing settings
    const removeConsoleLogsTimeoutRef = useRef(null);
    const removeConsoleLogFromIframe = () => {
        const frameRef = document.getElementById(iframeId);
        if (frameRef && frameRef.contentWindow) {
            frameRef.contentWindow.console.log = function() {};
        } else {
            removeConsoleLogsTimeoutRef.current = setTimeout(removeConsoleLogFromIframe, 100);
        }
    };
    useEffect(() => {
        removeConsoleLogFromIframe();
        return () => {
            if (removeConsoleLogsTimeoutRef.current) {
                clearTimeout(removeConsoleLogsTimeoutRef.current);
            }
        };
    }, [iframeId]);

    // Detect changes whenever we re-render
    const newStringifiedSettings = JSON.stringify(customisationVars);
    if (stringifiedSettingsRef.current !== null && newStringifiedSettings !== stringifiedSettingsRef.current) {
        stringifiedSettingsRef.current = newStringifiedSettings;
        hasStartedRef.current = false;

        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
            // Reload our iframe
            const frameRef = document.getElementById(iframeId);
            if (frameRef) {
                frameRef.contentWindow.location.reload();
            }
        }, 1500);
    }

    return (
        <iframe
            title="Particle Effects Preview"
            id={iframeId}
            src={`/disruptor/particles/`}
            style={{ width: '100%', height: '100%', border: 'none', pointerEvents: props.clickable ? 'auto' : 'none' }}
        ></iframe>
    );
}

export default ParticleEffects;
